import { useEffect, useRef } from "react";
import { useProductTourContext } from "./ProductTourProvider";
import type { TourStep } from "@/lib/product-tour/tour-steps";

/**
 * Registra o tour da página atual. Ele aparece no menu do perfil, junto do tour global.
 *
 * Antes era um botão "?" na barra superior; depois virou um componente invisível passado no slot
 * `actions` do cabeçalho. Com a barra reservada a switcher, sino e perfil, o slot deixou de existir
 * — e como o componente só era MONTADO porque a barra o renderizava, tirar o slot sem mais nada
 * teria sumido com o tour de todas as páginas, em silêncio. Hook resolve sem depender de onde a
 * página se desenha: roda em qualquer caminho de render, inclusive carregando.
 *
 * `onBeforeStart` roda antes do tour — pra páginas com aba ou wizard interno cujo passo alvo só
 * existe num estado (ex: Prótese só mostra os filtros na aba "Pedidos").
 */
export function useTourDaPagina(steps: TourStep[], onBeforeStart?: () => void) {
  const { registrarTourDaPagina } = useProductTourContext();

  // A página passa `onBeforeStart` como arrow inline, nova a cada render. Se ela entrasse nas
  // dependências, registrar mudaria o estado do provider, que re-renderiza a página, que registra
  // de novo — em laço. Pela ref o registro fica estável e ainda chama a versão mais recente.
  const antesRef = useRef(onBeforeStart);
  antesRef.current = onBeforeStart;
  const temAntes = !!onBeforeStart;

  // Assinatura pelos alvos e títulos, não só tamanho e primeiro alvo: o Catálogo troca o segundo
  // passo conforme a aba, e comparar só aqueles dois deixava o tour preso na aba em que se abriu.
  const assinatura = steps.map((s) => `${s.target}\u0000${s.title}`).join("\u0001");

  useEffect(() => {
    if (steps.length === 0) {
      registrarTourDaPagina(null);
      return;
    }
    registrarTourDaPagina({ steps, onBeforeStart: temAntes ? () => antesRef.current?.() : undefined });
    // Ao sair da página o registro cai, senão o menu ofereceria o tour da tela anterior.
    return () => registrarTourDaPagina(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinatura, temAntes, registrarTourDaPagina]);
}
